<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      v-if="isNewVerifications && total !== 0"
      :icon="AddIcon"
      :label="$t('pages.images.verifications.verificationModal.buttonText')"
      @click="openOnVerificationId = undefined; openVerificationModal = true; "
    />
    <export
      v-if="isAdmin"
      url="/verifications/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t(`csv.title.verifications.${isNewVerifications ? 'new' : 'old'}`)"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    >
      <ExportLink
        url="/verifications/download-zip"
        :filter="totalFilter"
        :label="$t('toolbar.export.zipDownload')"
        :pre-export-check="handleDownloadLimit"
        :class="{'disabled-link mt-5' : isExportDisabled, 'mt-5': !isExportDisabled}"
      />
    </export>
    <photos-download-warning-popup
      :open="openWarningPopup"
      :photo-count="total"
      :is-verifications="true"
      @confirm="handleConfirm"
      @cancel="handleClose"
    />
  </toolbar>
  <verification-modal
    v-if="isNewVerifications"
    v-model:open="openVerificationModal"
    :provider-params="providerParams"
    :open-on-verification-id="openOnVerificationId"
    @close="refetch"
  />
  <edit-verification-modal
    v-else-if="editVerification"
    v-model:open="openEditVerificationModal"
    v-model:verification="editVerification"
    @close="refetch"
  />
  <data-table
    v-model:sort-by="sortKey"
    v-model:sort-direction="sortDirection"
    class="mb-auto ml-12"
    :busy="loading"
    :items="records"
    :columns="columns"
    :row-click="openModal"
    :visible-columns="visibleColumns"
    :actions="actions"
  >
    <template #pictureBack="{ item }">
      <file
        v-slot="{ url, type }"
        :src="item.pictureBack && thumbnailUrl(item.pictureBack, { width: 500, height: 500 })"
        class="py-4 h-full"
      >
        <box :aspect="1" class="medium m-auto">
          <medium
            v-if="url"
            class="w-auto h-full"
            :url="url"
            :type="type"
          />
        </box>
      </file>
    </template>
  </data-table>
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
  />
</template>

<script lang="ts" setup>
import {
  collOptionsProvider,
  enumOptionsProvider,
  type Filter as FilterType,
  useProvider,
  type ProviderParams, withExtraOptions,
} from '@utils/table/hooks/provider';
import {
  ref, reactive, computed, unref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddIcon from '@images/panes/add.svg?component';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import File from '@utils/File.vue';
import Medium from '@utils/Medium.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import Action from '@utils/toolbar/Action.vue';
import type { Filter } from '@utils/filter/Filter.vue';
import type { Option } from '@utils/filter/filterTypes/Dropdown.vue';
import VerificationModal from '@pages/images/verifications/VerificationModal.vue';
import PhotosDownloadWarningPopup from '@pages/images/photos/PhotosDownloadWarningPopup.vue';
import Box from '@utils/Box.vue';
import EditVerificationModal from '@pages/images/verifications/EditVerificationModal.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import ExportLink from '@utils/toolbar/panes/ExportLink.vue';
import { format } from 'date-fns';
import { useTableFields } from '@hooks/table-configuration';
import { thumbnailUrl } from '@/utils/api';
import type {
  Campaign, HydraCollection, License, Region, Verification,
} from '@/entities/Entities';
import { useStore } from '@/store';
import { useQueryRef } from '@/hooks/url';
import { useNotify } from '@/components/utils/notifications';

interface Props {
  isNewVerifications: boolean;
}

const props = defineProps<Props>();

const openVerificationModal = ref(false);
const openEditVerificationModal = ref(false);
const openWarningPopup = ref(false);
const openOnVerificationId = ref();
const editVerification = ref<Verification | undefined>(undefined);

const store = useStore();

const isAdmin = store.getters['auth/IS_ADMIN'];

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef<FilterType>('filter', {});
const fixedFilter: FilterType = props.isNewVerifications
  ? {
    status: 'NeedsVerification',
  }
  : {
    'not[status]': 'NeedsVerification',
  };
const totalFilter = computed(() => ({ ...fixedFilter, ...filter.value }));

const sortKey = useQueryRef<string | undefined>('sortKey', props.isNewVerifications ? 'createdAt' : 'updatedAt');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const { t } = useI18n();

const VerificationStatus = ref<Option[]>();
enumOptionsProvider('/enums/VerificationStatus', (item) => ({
  label: t(`driver.verificationStatus.${item}`),
  value: item,
}))().then((val) => {
  VerificationStatus.value = val.filter((el) => el.value !== 'NeedsVerification');
});

const openModal = (item: Verification, column?: Column): void => {
  if (props.isNewVerifications) {
    openVerificationModal.value = true;
    openOnVerificationId.value = item.id;
  } else {
    openEditVerificationModal.value = true;
    editVerification.value = { ...item };
  }
};

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

const actions = props.isNewVerifications
  ? [
    {
      title: t('pages.images.verifications.actions.verify'),
      action: (item: Verification) => {
        openVerificationModal.value = true;
        openOnVerificationId.value = item.id;
      },
    },
  ]
  : [
    {
      title: t('pages.images.verifications.actions.edit'),
      action: (item: Verification) => {
        openEditVerificationModal.value = true;
        editVerification.value = { ...item };
      },
    },
  ];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.images.verifications.fieldGroups.general'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'driver.user.firstName',
    title: t('user.firstName'),
    sortable: true,
    visible: true,
    group: t('pages.images.verifications.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.images.verifications.filterGroups.general'),
      options: {
        label: t('user.firstName'),
      },
    },
  },
  {
    key: 'driver.user.lastName',
    title: t('user.lastName'),
    group: t('pages.images.verifications.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.images.verifications.filterGroups.general'),
      options: {
        label: t('user.lastName'),
      },
    },
  },
  {
    key: 'driver.user.email',
    title: t('user.email'),
    group: t('pages.images.verifications.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.images.verifications.filterGroups.general'),
      options: {
        label: t('user.email'),
      },
    },
  },
  {
    key: 'driver.canton',
    title: t('driver.canton'),
    group: t('pages.images.verifications.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => t(`canton.${v}`),
    filter: {
      key: 'driver.canton',
      type: 'dropdown',
      group: t('pages.images.verifications.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/Canton', (v) => ({
          label: t(`canton.${v}`),
          value: v,
        })),
        label: t('driver.canton'),
      },
    },
  },
  ...((props.isNewVerifications
    ? []
    : [
      {
        key: 'status',
        csvTranslations: {
          null: t('driver.verificationStatus.null'),
          Denied: t('driver.verificationStatus.Denied'),
          Verified: t('driver.verificationStatus.Verified'),
          NeedsVerification: t('driver.verificationStatus.NeedsVerification'),
        },
        title: t('verification.status'),
        group: t('pages.images.verifications.fieldGroups.general'),
        sortable: true,
        visible: true,
        formatter: (value: string) => t(`driver.verificationStatus.${value}`),
        filter: {
          type: 'dropdown',
          group: t('pages.images.verifications.filterGroups.general'),
          options: {
            options: VerificationStatus,
            label: t('verification.status'),
          },
        },
      },
    ]) as ColumnDefinition[]),
  {
    key: 'pictureBack',
    title: t('photo'),
    group: t('pages.images.verifications.fieldGroups.general'),
    sortable: false,
    visible: true,
  },
  {
    key: 'campaignRegistration.campaign.name',
    title: t('campaign.DEFAULT'),
    group: t('pages.images.photos.fieldGroups.general'),
    sortable: false,
    to: (verification: Verification) =>
      (verification?.campaignRegistration?.campaign?.group?.id
        ? {
          name: 'campaign-group-details',
          params: { campaignGroupId: verification?.campaignRegistration?.campaign?.group?.id },
        }
        : undefined),
    filter: {
      key: 'campaignRegistration.campaign.group',
      type: 'dropdown',
      group: t('pages.images.photos.filterGroups.general'),
      options: {
        label: t('campaign.DEFAULT'),
        searchable: true,
        minChars: 2,
        filterResults: false,
        clearOnSelect: true,
        delay: 0,
        resolveOnLoad: true,
        extraOption: {
          label: t('verification.campaign.no'),
          value: 'null',
        },
        options: (query: string, select: any) => {
          const input = query || select.modelValue;

          if (!input) return Promise.resolve([]);
          const filterQuery: ProviderParams = {
            filter: query ? { name: input } : { id: select.modelValue },
          };
          return collOptionsProvider<Campaign>('/campaign_groups', filterQuery, (value) => ({
            label: value.name,
            value: value.id,
          }))();
        },
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'null') {
            delete m['campaignRegistration.campaign.group'];
            m['exists[campaignRegistration]'] = false;
          } else {
            delete m['exists[campaignRegistration]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[campaignRegistration]' in m) {
            return 'null';
          }
          return m[key];
        },
      },
    },
  },
  {
    key: 'licenseSeatRegistration.licenseSeat.license.company.name',
    title: t('licenses.company'),
    group: t('pages.images.photos.filterGroups.general'),
    sortable: true,
    to: (verification: Verification) =>
      (verification?.licenseSeatRegistration?.licenseSeat?.license?.company?.id
        ? {
          name: 'licenses-company-details',
          params: { companyId: verification?.licenseSeatRegistration?.licenseSeat?.license?.company?.id },
        }
        : undefined),
    filter: {
      key: 'licenseSeatRegistration.licenseSeat.license.company',
      type: 'dropdown',
      group: t('pages.images.photos.filterGroups.general'),
      options: {
        label: t('licenses.company'),
        searchable: true,
        minChars: 2,
        filterResults: false,
        clearOnSelect: true,
        delay: 0,
        resolveOnLoad: true,
        extraOption: {
          label: t('verification.licenseSeat.no'),
          value: 'null',
        },
        options: (query: string, select: any) => {
          const input = query || select.modelValue;

          if (!input) return Promise.resolve([]);
          const filterQuery: ProviderParams = {
            filter: query ? { name: input } : { id: select.modelValue },
          };
          return collOptionsProvider<Campaign>('/companies', filterQuery, (value) => ({
            label: value.name,
            value: value.id,
          }))();
        },
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'null') {
            delete m['licenseSeatRegistration.licenseSeat.license.company'];
            m['exists[licenseSeatRegistration]'] = false;
          } else {
            delete m['exists[licenseSeatRegistration]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[licenseSeatRegistration]' in m) {
            return 'null';
          }
          return m[key];
        },
      },
    },
  },
  {
    key: 'exists[licenseSeatRegistration]',
    title: t('verification.forBusiness'),
    group: t('pages.images.verifications.filterGroups.general'),
    sortable: false,
    visible: true,
    computed: (item: Verification) => ((item.licenseSeatRegistration !== null) ? t('yes') : t('no')),
    filter: {
      type: 'bool',
      group: t('pages.campaignList.filterGroups.general'),
      options: {
        label: t('verification.forBusiness'),
      },
    },
  },
  {
    key: 'driver.region.name',
    title: t('region'),
    group: t('pages.images.verifications.fieldGroups.general'),
    sortable: false,
    filter: {
      key: 'driver.region',
      type: 'dropdown',
      group: t('pages.images.verifications.filterGroups.general'),
      options: {
        label: t('region'),
        options: collOptionsProvider<Region>('/regions', {
          sort: {
            sortKey: 'name',
            sortDirection: 'ASC',
          },
        }, (value) => ({
          label: value.name,
          value: value.id,
        })),
      },
    },
  },
  {
    key: 'licenseSeatRegistration.licenseSeat.license.startDate',
    title: t('licenses.licenseStartDate'),
    group: t('pages.images.verifications.fieldGroups.general'),
    sortable: true,
    formatter: (v: string) => v && format(new Date(v), 'dd.MM.yyyy - HH:mm:ss'),
    visible: true,
    filter: {
      type: 'datetime',
      group: t('pages.images.verifications.filterGroups.general'),
      options: {
        label: t('licenses.licenseStartDate'),
      },
    },
  },
  {
    key: 'licenseSeatRegistration.licenseSeat.license.endDate',
    title: t('licenses.licenseEndDate'),
    group: t('pages.images.verifications.fieldGroups.general'),
    sortable: true,
    formatter: (v: string) => v && format(new Date(v), 'dd.MM.yyyy - HH:mm:ss'),
    visible: true,
    filter: {
      type: 'datetime',
      group: t('pages.images.verifications.filterGroups.general'),
      options: {
        label: t('licenses.licenseEndDate'),
      },
    },
  },
  {
    key: 'licenseSeatRegistration.licenseSeat.license',
    title: t('licenses.license'),
    sortable: false,
    visible: true,
    group: t('pages.licenseList.fieldGroups.general'),
    computed: (v: Verification) => {
      const companyName = v?.licenseSeatRegistration?.licenseSeat?.license?.company?.name ?? '';
      const startDate = v?.licenseSeatRegistration?.licenseSeat?.license?.startDate;
      const endDate = v?.licenseSeatRegistration?.licenseSeat?.license?.endDate;
      if (startDate && endDate) {
        return `${companyName}: ${format(new Date(startDate), 'dd.MM.yyyy') ?? ''} - ${format(new Date(endDate), 'dd.MM.yyyy') ?? ''}`;
      }
      return companyName;
    },
    to: (verification: Verification) =>
      (verification?.licenseSeatRegistration?.licenseSeat?.license?.company?.id
        ? {
          name: 'company-license-details',
          params: { companyId: verification.licenseSeatRegistration.licenseSeat.license.company.id },
          query: {
            license: JSON.stringify(verification.licenseSeatRegistration.licenseSeat.license['@id']),
          },
        }
        : undefined),
    filter: {
      key: 'licenseSeatRegistration.licenseSeat.license',
      type: 'dropdown',
      group: t('pages.licenseList.filterGroups.general'),
      options: {
        searchable: true,
        minChars: 1, // in the future would probably need to raise this value
        filterResults: false,
        clearOnSelect: true,
        delay: 500,
        createOption: false,
        resolveOnLoad: true,
        noOptionsText: t('inputSearchString'),
        options: (query: string, select: any) => {
          const input = query || select.modelValue;

          if (!input) return Promise.resolve([]);
          const filterQuery: ProviderParams = {
            filter: query ? { 'company.name': input } : { id: select.modelValue },
          };
          return collOptionsProvider<License>('/licenses', filterQuery, (value) => ({
            // eslint-disable-next-line max-len
            label: `${value.company?.name}: ${value.startDate && format(new Date(value.startDate), 'dd.MM.yyyy')} - ${value.endDate && format(new Date(value.endDate), 'dd.MM.yyyy')}`,
            value: value.id,
          }))();
        },
        label: t('licenses.license'),
      },
    },
  },
  {
    key: 'updatedAt',
    title: t('updatedAt'),
    group: t('pages.images.verifications.fieldGroups.general'),
    sortable: true,
    visible: !props.isNewVerifications,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.images.verifications.filterGroups.general'),
      options: {
        label: t('updatedAt'),
      },
    },
  },
  {
    key: 'oldestFileCreatedAt',
    title: t('pages.images.verifications.oldestFileCreatedAt'),
    group: t('pages.images.verifications.fieldGroups.general'),
    sortable: true,
    visible: props.isNewVerifications,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.images.verifications.filterGroups.general'),
      options: {
        label: t('pages.images.verifications.oldestFileCreatedAt'),
      },
    },
  },
  {
    key: 'createdAt',
    title: t('pages.images.verifications.createdAt'),
    group: t('pages.images.verifications.fieldGroups.general'),
    sortable: true,
    visible: props.isNewVerifications,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.images.verifications.filterGroups.general'),
      options: {
        label: t('pages.images.verifications.createdAt'),
      },
    },
  },
  {
    key: 'campaignRegistration.campaign.actualStartDate',
    title: t('pages.images.verifications.filterGroups.campaign.startDate'),
    group: t('pages.images.verifications.fieldGroups.general'),
    sortable: true,
    formatter: (v: string) => v && format(new Date(v), 'dd.MM.yyyy - HH:mm:ss'),
    visible: false,
    filter: {
      type: 'datetime',
      group: t('pages.images.verifications.filterGroups.general'),
      options: {
        label: t('pages.images.verifications.filterGroups.campaign.startDate'),
      },
    },
  },
  {
    key: 'campaignRegistration.campaign.actualEndDate',
    title: t('pages.images.verifications.filterGroups.campaign.endDate'),
    group: t('pages.images.verifications.fieldGroups.general'),
    sortable: true,
    formatter: (v: string) => v && format(new Date(v), 'dd.MM.yyyy - HH:mm:ss'),
    visible: false,
    filter: {
      type: 'datetime',
      group: t('pages.images.verifications.filterGroups.general'),
      options: {
        label: t('pages.images.verifications.filterGroups.campaign.endDate'),
      },
    },
  },
];

const visibleColumns = useTableFields(
  columns,
);

const {
  data: items,
  loading,
  refetch,
} = useProvider<HydraCollection<Verification>>('/verifications', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const totalVerificationPhotos = computed(() => total.value && total.value * 5);
const maxDownloadLimit = computed(() => window.env.VITE_MAX_DOWNLOAD_LIMIT || import.meta.env.VITE_MAX_DOWNLOAD_LIMIT);
const warningDownloadLimit = computed(() => window.env.VITE_WARNING_DOWNLOAD_LIMIT || import.meta.env.VITE_WARNING_DOWNLOAD_LIMIT);
const isExportDisabled = computed(() => totalVerificationPhotos.value && totalVerificationPhotos.value > maxDownloadLimit.value);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  }));

const notify = useNotify();
let resolveDownloadPromise: () => void;
let rejectDownloadPromise: () => void;

const handleDownloadLimit = async (): Promise<boolean> => {
  // if the total number of photos is greater than the warning limit but lower than the download limit, show a warning popup
  if (totalVerificationPhotos.value > warningDownloadLimit.value && !isExportDisabled.value) {
    openWarningPopup.value = true;

    return new Promise((resolve) => {
      resolveDownloadPromise = () => {
        openWarningPopup.value = false;
        resolve(true);
      };
      rejectDownloadPromise = () => {
        openWarningPopup.value = false;
        resolve(false);
      };
    });
  // if the total number of photos is greater than the download limit, show an error notification
  } if (isExportDisabled.value) {
    notify?.({
      variant: 'error',
      title: t('toolbar.export.downloadLimitVerificationsErrror', { limit: maxDownloadLimit.value / 5, total: maxDownloadLimit.value }),
      duration: 4000,
    });
    return false;
  }
  return true;
};

const handleClose = () => {
  openWarningPopup.value = false;
  rejectDownloadPromise();
};

const handleConfirm = () => {
  resolveDownloadPromise();
};

</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}

.disabled-link {
  @apply text-light-gray;
  @apply cursor-not-allowed;
}

:deep() {

}
</style>
